import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AreaTitle from '../../../components/areaTitle/AreaTitle';
import WhatsAppPlatformStatusCard from './components/WhatsAppPlatformStatusCard';
import WhatsAppSummaryCards from './components/WhatsAppSummaryCards';
import WhatsAppCategoryBreakdown from './components/WhatsAppCategoryBreakdown';
import WhatsAppCompaniesTable from './components/WhatsAppCompaniesTable';
import useAdminWhatsApp from './hooks/useAdminWhatsApp';

/**
 * AdminWhatsApp - Consumo do número compartilhado da plataforma por empresa (Admin Geral)
 *
 * Responsabilidades:
 * - Consome useAdminWhatsApp() para o status da instância compartilhada, os cards de KPI
 *   e a tabela paginada/filtrada de empresas (overview.php + companies.php)
 * - Renderiza AreaTitle + botão "Atualizar" + card de status + cards de KPI + tabela com
 *   filtros e paginação server-side, com os filtros vivendo na URL para que o link possa
 *   ser compartilhado
 */
function AdminWhatsApp() {
  const {
    overview,
    overviewLoading,
    overviewError,
    categories,
    sendsByCategory,
    companies,
    pagination,
    companiesLoading,
    companiesError,
    days,
    setDays,
    searchInput,
    setSearchInput,
    origin,
    setOrigin,
    includeTest,
    setIncludeTest,
    sort,
    setSort,
    setPage,
    refresh,
  } = useAdminWhatsApp();

  const error = overviewError || companiesError;

  return (
    <Box sx={{ backgroundColor: '#F8F9FC', minHeight: '100vh', p: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <AreaTitle
          Title="Admin Geral · WhatsApp"
          Icon={<WhatsAppIcon />}
          Description="Consumo do número compartilhado da plataforma por empresa."
        />

        <Button
          variant="outlined"
          startIcon={<RefreshOutlinedIcon />}
          onClick={refresh}
          disabled={overviewLoading || companiesLoading}
        >
          Atualizar
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {overviewLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <>
          <WhatsAppPlatformStatusCard overview={overview} />
          <WhatsAppSummaryCards overview={overview} days={days} />
        </>
      )}

      <WhatsAppCategoryBreakdown
        categories={categories}
        sendsByCategory={sendsByCategory}
        days={days}
        isLoading={overviewLoading}
      />

      <WhatsAppCompaniesTable
        companies={companies}
        isLoading={companiesLoading}
        error={companiesError}
        pagination={pagination}
        categories={categories}
        filters={{
          searchInput,
          onSearchChange: setSearchInput,
          days,
          onDaysChange: setDays,
          origin,
          onOriginChange: setOrigin,
          includeTest,
          onIncludeTestChange: setIncludeTest,
          sort,
          onSortChange: setSort,
        }}
        onPageChange={setPage}
      />
    </Box>
  );
}

export default AdminWhatsApp;
